import { LoadingSpinner } from "components/LoadingSpinner";
import { centsToDollars, parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { useEffect, useMemo, useState } from "react";
import {
  useGetAvailableCompetitors,
  useGetMarketPriceAnalysis,
  useGetPayersWithMarketData,
} from "store/queries/marketPrices";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetPayerTermsForAccountQuery } from "store/queries/providerProcedure";
import styled from "styled-components";
import {
  IMarketPriceAnalysisRow,
  IMarketPriceRecommendation,
} from "types/marketPriceAnalysis";
import { IPayerTerm, IPayerWithTerms } from "types/payerTerms";

import { exportToCSV } from "./helpers";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;

  .p-multiselect {
    .p-multiselect-label {
      display: flex;
      align-items: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .competitor-multiselect-panel {
    .p-multiselect-header {
      .p-checkbox {
        margin-right: 0.5rem;
      }
    }

    .p-multiselect-items {
      max-height: 300px;
    }
  }

  .p-datatable {
    .bg-primary-100 {
      background-color: #f0f6ff !important;

      td {
        font-weight: 600;
        border-top: 2px solid var(--primary-color);
      }
    }
  }
`;

const StyledRecommendationCard = styled.div<{
  type: "high" | "medium" | "low";
}>`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid
    ${(props) => {
      switch (props.type) {
        case "high":
          return "#D4EDDA";
        case "medium":
          return "#FFF3CD";
        case "low":
          return "#F8D7DA";
        default:
          return "#E9ECEF";
      }
    }};
  background-color: ${(props) => {
    switch (props.type) {
      case "high":
        return "#D4EDDA";
      case "medium":
        return "#FFF3CD";
      case "low":
        return "#F8D7DA";
      default:
        return "#F8F9FA";
    }
  }};

  .recommendation-icon {
    color: ${(props) => {
      switch (props.type) {
        case "high":
          return "#155724";
        case "medium":
          return "#856404";
        case "low":
          return "#721C24";
        default:
          return "#495057";
      }
    }};
  }

  .recommendation-title {
    color: ${(props) => {
      switch (props.type) {
        case "high":
          return "#155724";
        case "medium":
          return "#856404";
        case "low":
          return "#721C24";
        default:
          return "#495057";
      }
    }};
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 8px;
  }

  .recommendation-description {
    color: ${(props) => {
      switch (props.type) {
        case "high":
          return "#155724";
        case "medium":
          return "#856404";
        case "low":
          return "#721C24";
        default:
          return "#495057";
      }
    }};
    font-size: 14px;
  }
`;

function formatTermLabel(term: IPayerTerm): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const end = term.endDate ? fmt(term.endDate) : "Open";
  return `${fmt(term.effectiveDate)} → ${end}`;
}

function formatEffectiveDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Keys encode (payerId, termId) as "payerId:termId". Term ids from the backend
// contain colons (ISO timestamps in the synthetic id), so we must only split on
// the FIRST colon — anything after is the full term id.
function parsePayerTermKey(key: string): {
  payerId: number;
  termId: string;
} {
  const idx = key.indexOf(":");
  if (idx < 0) return { payerId: Number(key), termId: "" };
  return { payerId: Number(key.slice(0, idx)), termId: key.slice(idx + 1) };
}

interface IMarketPriceAnalysisProps {
  accountId: string;
  specialty: string;
}

export function MarketPriceAnalysis({
  accountId,
  specialty,
}: IMarketPriceAnalysisProps) {
  const [selectedPayerTermKey, setSelectedPayerTermKey] = useState<
    string | null
  >(null);
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([]);
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  const selectedPayerId = useMemo<number | null>(() => {
    if (!selectedPayerTermKey) return null;
    const { payerId } = parsePayerTermKey(selectedPayerTermKey);
    return Number.isNaN(payerId) ? null : payerId;
  }, [selectedPayerTermKey]);

  const selectedTermId = useMemo<string | null>(() => {
    if (!selectedPayerTermKey) return null;
    return parsePayerTermKey(selectedPayerTermKey).termId || null;
  }, [selectedPayerTermKey]);

  const { data: payersData, isLoading: isLoadingPayers } =
    useGetAllPayersQuery();
  const { data: payersWithMarketData, isLoading: isLoadingPayersWithData } =
    useGetPayersWithMarketData(specialty ? { specialty } : undefined);
  const { data: availableCompetitors, isLoading: isLoadingCompetitors } =
    useGetAvailableCompetitors(
      selectedPayerId || specialty
        ? { payerId: selectedPayerId || undefined, specialty }
        : undefined
    );
  const { data: payerTermsData, isLoading: isLoadingPayerTerms } =
    useGetPayerTermsForAccountQuery({ accountId, specialty });
  const [
    getMarketPriceAnalysis,
    { data: analysisData, isLoading: isLoadingAnalysis },
  ] = useGetMarketPriceAnalysis();

  const selectedPayerTerms = useMemo<IPayerWithTerms | undefined>(() => {
    if (!selectedPayerId || !payerTermsData) return undefined;
    return payerTermsData.find((p) => p.payerId === selectedPayerId);
  }, [selectedPayerId, payerTermsData]);

  // Open-panel item renderer: under a payer-name group header we only need the
  // date range. The collapsed selected value uses each option's `label`
  // ("Cigna Apr 20, 2026") so the native Dropdown styling stays intact.
  const payerItemTemplate = (option: { range: string }) => (
    <span>{option.range}</span>
  );

  // Clear competitors and reset pagination when payer changes.
  useEffect(() => {
    setSelectedCompetitors([]);
    setFirst(0);
  }, [selectedPayerId, specialty]);

  // Get unique competitor names from available competitors
  const competitorOptions = useMemo(() => {
    if (!availableCompetitors) return [];

    // Sort competitors: selected first (alphabetically), then unselected (alphabetically)
    const sortedCompetitors = [...availableCompetitors].sort((a, b) => {
      const aSelected = selectedCompetitors.includes(a);
      const bSelected = selectedCompetitors.includes(b);

      // If both selected or both unselected, sort alphabetically
      if (aSelected === bSelected) {
        return a.localeCompare(b);
      }

      // Selected items come first
      return aSelected ? -1 : 1;
    });

    return sortedCompetitors.map((name: string) => ({
      label: name,
      value: name,
    }));
  }, [availableCompetitors, selectedCompetitors]);

  // Grouped options: each payer-with-market-data is a group; items are its
  // contract terms (or a single no-term item for payers with no active contracts).
  const groupedPayerOptions = useMemo(() => {
    if (!payersData?.data || !payersWithMarketData) return [];
    return payersData.data
      .filter(
        (payer: any) =>
          !payer.archivedAt &&
          payer.name !== "Clear Health" &&
          payersWithMarketData.includes(payer.id)
      )
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((payer: any) => {
        const pt = payerTermsData?.find((p) => p.payerId === payer.id);
        const items =
          pt && pt.terms.length > 0
            ? pt.terms.map((term) => ({
                // `label` drives the selected-value display ("Cigna Apr 20, 2026");
                // `range` is what `itemTemplate` renders inside the open panel
                // (just the date range, since the payer name is in the group header).
                label: `${payer.name} ${formatEffectiveDate(
                  term.effectiveDate
                )}`,
                range: formatTermLabel(term),
                value: `${payer.id}:${term.id}`,
              }))
            : [
                {
                  label: payer.name,
                  range: payer.name,
                  value: `${payer.id}:`,
                },
              ];
        return { label: payer.name, payerId: payer.id, items };
      });
  }, [payersData, payersWithMarketData, payerTermsData]);

  const hasAnyPayer = groupedPayerOptions.length > 0;

  const selectedTermContractIds = useMemo<string[]>(() => {
    if (!selectedTermId || !selectedPayerTerms) return [];
    return (
      selectedPayerTerms.terms.find((t) => t.id === selectedTermId)
        ?.contractFeeScheduleIds ?? []
    );
  }, [selectedTermId, selectedPayerTerms]);

  // Fetch analysis when selections or term changes
  useEffect(() => {
    if (
      selectedPayerId &&
      selectedCompetitors &&
      selectedCompetitors.length > 0
    ) {
      setFirst(0);
      getMarketPriceAnalysis({
        payerId: selectedPayerId,
        specialty,
        competitorNames: selectedCompetitors,
        limit: 100,
        ...(selectedTermContractIds.length > 0
          ? { contractFeeScheduleIds: selectedTermContractIds }
          : {}),
      });
    }
  }, [
    selectedPayerId,
    selectedCompetitors,
    specialty,
    selectedTermContractIds,
    getMarketPriceAnalysis,
  ]);

  const formatCurrency = (value: number) => {
    return parsePrice(centsToDollars(value));
  };

  const formatPercentage = (value: number) => {
    if (value === 0) return "0.00%";
    return `${(value * 100).toFixed(2)}%`;
  };

  const handleDownloadData = async () => {
    if (!analysisData || !analysisData.rows.length) return;

    try {
      setIsDownloading(true);

      // Format the data for CSV export (excluding averages row)
      const csvData = analysisData.rows.map((row: IMarketPriceAnalysisRow) => ({
        "CPT Code": row.cptCode,
        Volume: row.volume || 0,
        "Your Rate": formatCurrency(row.yourRate),
        "Your avg commercial":
          row.yourAverageCommercialRate > 0
            ? formatCurrency(row.yourAverageCommercialRate)
            : "N/A",
        [selectedCompetitors.length === 1 ? "Competitors" : "Competitors avg"]:
          formatCurrency(row.marketAverage),
        Median: formatCurrency(row.marketMedian),
        Min: formatCurrency(row.marketMin),
        Max: formatCurrency(row.marketMax),
        [selectedCompetitors.length === 1
          ? "Your rate → Competitors"
          : "Your rate → Competitors avg"]: formatPercentage(
          row.yourRateToMarketAverage
        ),
        "Your rate → CMS":
          row.yourMedicareRate > 0
            ? formatPercentage(row.yourRateToMedicareAverage)
            : "N/A",
        [selectedCompetitors.length === 1
          ? "Competitors → CMS"
          : "Competitors avg → CMS"]:
          row.yourMedicareRate > 0
            ? formatPercentage(row.marketAverageToMedicareAverage)
            : "N/A",
      }));

      const payerName =
        groupedPayerOptions.find(
          (g: { payerId: number; label: string }) =>
            g.payerId === selectedPayerId
        )?.label || "Unknown";
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `market_price_analysis_${payerName}_vs_${selectedCompetitors.length}_competitors_${timestamp}.csv`;

      exportToCSV(csvData, filename);
    } catch (error) {
      console.error("Error downloading data:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Create paginated table data with averages row
  const tableData = useMemo(() => {
    if (!analysisData || analysisData.rows.length === 0) return [];

    // Get the current page of data
    const paginatedRows = analysisData.rows.slice(first, first + rows);

    // Add the average row at the bottom
    const dataWithAverages = [...paginatedRows];
    dataWithAverages.push({
      cptCode: "Average",
      yourRate: analysisData.averages.yourRate,
      yourAverageCommercialRate:
        analysisData.averages.yourAverageCommercialRate,
      yourMedicareRate: analysisData.averages.yourMedicareRate,
      marketAverage: analysisData.averages.marketAverage,
      marketMedian: analysisData.averages.marketMedian,
      marketMin: analysisData.averages.marketMin,
      marketMax: analysisData.averages.marketMax,
      yourRateToMarketAverage: analysisData.averages.yourRateToMarketAverage,
      yourRateToMedicareAverage:
        analysisData.averages.yourRateToMedicareAverage,
      marketAverageToMedicareAverage:
        analysisData.averages.marketAverageToMedicareAverage,
      isAverageRow: true,
    } as any);

    return dataWithAverages;
  }, [analysisData, first, rows]);

  // Handle page change
  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  const recommendationIcon = (type: "high" | "medium" | "low") => {
    switch (type) {
      case "high":
        return (
          <i
            className="pi pi-check-circle recommendation-icon"
            style={{ fontSize: "20px" }}
          />
        );
      case "medium":
        return (
          <i
            className="pi pi-minus-circle recommendation-icon"
            style={{ fontSize: "20px" }}
          />
        );
      case "low":
        return (
          <i
            className="pi pi-times-circle recommendation-icon"
            style={{ fontSize: "20px" }}
          />
        );
      default:
        return (
          <i
            className="pi pi-question-circle recommendation-icon"
            style={{ fontSize: "20px" }}
          />
        );
    }
  };

  if (
    isLoadingPayers ||
    isLoadingCompetitors ||
    isLoadingPayersWithData ||
    isLoadingPayerTerms
  ) {
    return <LoadingSpinner />;
  }

  // Show message if no payers with data are available
  if (!hasAnyPayer) {
    return (
      <StyledContainer className="bg-white">
        <div className="p-4">
          <div className="flex gap-2 align-items-center mb-3">
            <i className="pi pi-chart-line text-2xl text-primary" />
            <p className="text-lg font-bold">Market Price Analysis</p>
          </div>
          <div className="text-center p-4 text-gray-500">
            No market price data available for any payers with the selected
            specialty. Please upload market price datasets to enable analysis.
          </div>
        </div>
      </StyledContainer>
    );
  }

  // Show message if no competitors are available when a payer is selected
  if (
    selectedPayerId &&
    (!competitorOptions || competitorOptions.length === 0)
  ) {
    return (
      <StyledContainer className="bg-white">
        <div className="p-4">
          <div className="flex gap-2 align-items-center mb-3">
            <i className="pi pi-chart-line text-2xl text-primary" />
            <p className="text-lg font-bold">Market Price Analysis</p>
          </div>
          <div className="flex gap-3 mb-4">
            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "340px" }}
            >
              <label className="text-sm font-semibold">Select Payer:</label>
              <Dropdown
                value={selectedPayerTermKey}
                options={groupedPayerOptions}
                onChange={(e) => setSelectedPayerTermKey(e.value)}
                optionLabel="label"
                optionValue="value"
                optionGroupLabel="label"
                optionGroupChildren="items"
                itemTemplate={payerItemTemplate}
                placeholder="Select a payer"
                showClear
                filter
              />
            </div>
          </div>
          <div className="text-center p-4 text-gray-500">
            No market price data available for the selected payer. Please upload
            market price datasets for this payer to enable analysis.
          </div>
        </div>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer className="bg-white">
      <div className="p-4">
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex gap-2 align-items-center">
            <i className="pi pi-chart-line text-2xl text-primary" />
            <p className="text-lg font-bold">Market Price Analysis</p>
          </div>
          {analysisData &&
            !isLoadingAnalysis &&
            selectedCompetitors &&
            selectedCompetitors.length > 0 &&
            analysisData.rows.length > 0 && (
              <Button
                label="Download"
                icon="pi pi-download"
                onClick={handleDownloadData}
                loading={isDownloading}
                className="p-button-outlined max-h-3rem max-w-12rem"
              />
            )}
        </div>

        <div className="flex gap-3 mb-4">
          <div className="flex flex-column gap-2" style={{ minWidth: "340px" }}>
            <label className="text-sm font-semibold">Select Payer:</label>
            <Dropdown
              value={selectedPayerTermKey}
              options={groupedPayerOptions}
              onChange={(e) => setSelectedPayerTermKey(e.value)}
              optionLabel="label"
              optionValue="value"
              optionGroupLabel="label"
              optionGroupChildren="items"
              itemTemplate={payerItemTemplate}
              placeholder="Select a payer"
              showClear
              filter
            />
          </div>

          <div
            className="flex flex-column gap-2 flex-grow-1"
            style={{ minWidth: "320px", maxWidth: "500px" }}
          >
            <label className="text-sm font-semibold">Select Competitors:</label>
            <MultiSelect
              value={selectedCompetitors || []}
              options={competitorOptions}
              onChange={(e) => setSelectedCompetitors(e.value || [])}
              placeholder={
                !selectedPayerId
                  ? "Select a payer first"
                  : "Select competitors to compare"
              }
              filter
              showClear
              className="w-full"
              panelClassName="competitor-multiselect-panel"
              showSelectAll
              emptyFilterMessage="No competitors found"
              display="comma"
              maxSelectedLabels={3}
              selectedItemsLabel="{0} competitors selected"
              disabled={!selectedPayerId || isLoadingCompetitors}
            />
          </div>
        </div>

        {isLoadingAnalysis && (
          <div className="flex justify-content-center p-5">
            <LoadingSpinner />
          </div>
        )}

        {analysisData &&
          !isLoadingAnalysis &&
          selectedCompetitors &&
          selectedCompetitors.length > 0 && (
            <>
              {tableData.length === 0 ? (
                <div className="text-center p-4 text-gray-500">
                  No data available.
                </div>
              ) : (
                <>
                  <DataTable
                    value={tableData}
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    className="mb-4"
                    rowClassName={(rowData) => ({
                      "bg-primary-100 font-bold": rowData.isAverageRow,
                    })}
                  >
                    <Column
                      field="cptCode"
                      header="CPT Code"
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="volume"
                      header="Volume"
                      body={(
                        rowData: IMarketPriceAnalysisRow & {
                          isAverageRow?: boolean;
                        }
                      ) => (rowData.isAverageRow ? "-" : rowData.volume || 0)}
                      style={{ width: "80px" }}
                    />
                    <Column
                      field="yourRate"
                      header="Your Rate"
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        formatCurrency(rowData.yourRate)
                      }
                      style={{ width: "120px" }}
                    />
                    <Column
                      field="yourAverageCommercialRate"
                      header={
                        <>
                          Your avg
                          <br />
                          commercial
                        </>
                      }
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        rowData.yourAverageCommercialRate > 0
                          ? formatCurrency(rowData.yourAverageCommercialRate)
                          : "N/A"
                      }
                      style={{ width: "120px" }}
                    />
                    <Column
                      field="marketAverage"
                      header={
                        selectedCompetitors.length === 1
                          ? "Competitors"
                          : "Competitors avg"
                      }
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        formatCurrency(rowData.marketAverage)
                      }
                      style={{ width: "120px" }}
                    />
                    <Column
                      field="marketMedian"
                      header="Median"
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        formatCurrency(rowData.marketMedian)
                      }
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="marketMin"
                      header="Min"
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        formatCurrency(rowData.marketMin)
                      }
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="marketMax"
                      header="Max"
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        formatCurrency(rowData.marketMax)
                      }
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="yourRateToMarketAverage"
                      header={
                        selectedCompetitors.length === 1
                          ? "Your rate → Competitors"
                          : "Your rate → Competitors avg"
                      }
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        formatPercentage(rowData.yourRateToMarketAverage)
                      }
                      style={{ width: "150px" }}
                    />
                    <Column
                      field="yourRateToMedicareAverage"
                      header="Your rate → CMS"
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        rowData.yourMedicareRate > 0
                          ? formatPercentage(rowData.yourRateToMedicareAverage)
                          : "N/A"
                      }
                      style={{ width: "130px" }}
                    />
                    <Column
                      field="marketAverageToMedicareAverage"
                      header={
                        selectedCompetitors.length === 1
                          ? "Competitors → CMS"
                          : "Competitors avg → CMS"
                      }
                      body={(rowData: IMarketPriceAnalysisRow) =>
                        rowData.yourMedicareRate > 0
                          ? formatPercentage(
                              rowData.marketAverageToMedicareAverage
                            )
                          : "N/A"
                      }
                      style={{ width: "160px" }}
                    />
                  </DataTable>
                  {analysisData.rows.length > rows && (
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={analysisData.rows.length}
                      onPageChange={onPageChange}
                    />
                  )}
                </>
              )}

              {analysisData.recommendations.length > 0 && (
                <div className="mt-4">
                  <div className="flex gap-2 align-items-center mb-3">
                    <i className="pi pi-chart-line text-xl text-primary" />
                    <p className="text-md font-bold">Recommended Actions</p>
                  </div>
                  {analysisData.recommendations.map(
                    (rec: IMarketPriceRecommendation, index: number) => (
                      <StyledRecommendationCard key={index} type={rec.type}>
                        <div className="flex gap-2 align-items-start">
                          {recommendationIcon(rec.type)}
                          <div className="flex-grow-1">
                            <div className="recommendation-title">
                              {rec.title}
                            </div>
                            <div className="recommendation-description">
                              {rec.description}
                            </div>
                          </div>
                        </div>
                      </StyledRecommendationCard>
                    )
                  )}
                </div>
              )}
            </>
          )}

        {!selectedPayerId && !isLoadingAnalysis && (
          <div className="text-center p-4 text-gray-500">
            Please select a payer to view the market price analysis.
          </div>
        )}

        {selectedPayerId &&
          (!selectedCompetitors || selectedCompetitors.length === 0) &&
          !isLoadingAnalysis && (
            <div className="text-center p-4 text-gray-500">
              Please select at least one competitor to view the market price
              analysis.
            </div>
          )}
      </div>
    </StyledContainer>
  );
}
